// The account panel's Company info tab for leadership and viewers (20 Oct). The panel itself and its
// Scout, Research and History tabs are the same for every role; `lib/roleViews.ts` picks this tab.
//
// Oversight, read-only: who owns the account, its deals, who has worked it and how recently, the
// flags with what they mean, and usage. The strip above already carries stage, pipeline, owners
// and last activity, so this tab gives the detail behind them rather than repeating them.

import type { BookAccount, Opportunity } from '../api/types';
import { contactDisplayName } from '../lib/contactName';
import { formatCount, formatMoney, httpsUrl } from '../lib/format';
import { lastOutbound, recentLostDeal } from '../book/derived';
import { hasSharedSlack, pastOpportunityDetail, pastOpportunityOf } from '../book/accountFlags';
import { OrgRow } from '../sidebar/HistoryTab';
import { dealOutcome, orgTrends, pastDeals } from '../sidebar/historyData';
import { outreachPeople } from '../sidebar/outreachPeople';
import { dealAmount, openOpportunities, q4Fact } from '../sidebar/panelFacts';
import { PanelSection } from '../sidebar/panelParts';
import { daysFrom, relativeDay } from '../ae/aeFacts';
import { leadDeal } from '../ae/aeSections';

function dayWithAge(day: string | null | undefined, today: string, empty: string): string {
  if (!day) return empty;
  return `${day} (${relativeDay(day, today)})`;
}

// One open deal, every field on its own line.
function DealCard({ deal, today }: { deal: Opportunity; today: string }) {
  const amount = dealAmount(deal);
  const overdue = deal.close_date != null && daysFrom(deal.close_date, today) < 0;
  const contact = deal.contact;
  return (
    <div className="aedeal">
      <div className="aedealname">{deal.name || 'Unnamed opportunity'}</div>
      <dl>
        <dt>Stage</dt><dd>{deal.stage || 'No stage'}</dd>
        <dt>Amount</dt><dd>{amount == null ? '—' : formatMoney(amount)}</dd>
        <dt>Close date</dt><dd className={overdue ? 'warn' : undefined}>{dayWithAge(deal.close_date, today, 'Not set')}{overdue ? ', overdue' : ''}</dd>
        <dt>Next step</dt><dd>{deal.next_step || 'None written'}</dd>
        {deal.owner ? <><dt>Deal owner</dt><dd>{deal.owner}</dd></> : null}
        {contact && (contact.name || contact.email) ? (
          <><dt>With</dt><dd>{contactDisplayName(contact.name, contact.email, '')}{contact.title ? <span className="muted"> · {contact.title}</span> : null}</dd></>
        ) : null}
      </dl>
    </div>
  );
}

export function OversightCompanyInfo({ row, today }: { row: BookAccount; today: string }) {
  const open = openOpportunities(row);
  const lead = leadDeal(row);
  const past = pastDeals(row);
  const lastSdr = lastOutbound(row);
  const trends = orgTrends(row, outreachPeople(row));
  const pastOpportunity = pastOpportunityOf(row);
  const lost = recentLostDeal(row, new Date(today + 'T00:00:00Z'));
  const q4 = q4Fact(row);
  const slackHref = httpsUrl(row.pylon_slack_url);
  const hasFlags = hasSharedSlack(row) || pastOpportunity || lost || row.competitor || q4;

  return (
    <div className="aecompany">
      {/* Who owns it, and since when. */}
      <PanelSection title="Ownership">
        <dl className="aefacts">
          <dt>SDR</dt><dd>{row.sdr_owner || 'Unassigned'}</dd>
          <dt>AE</dt><dd>{row.account_owner || 'No account owner'}</dd>
          <dt>On the book since</dt><dd>{dayWithAge(row.added_at?.slice(0, 10), today, 'Not recorded')}</dd>
          {row.auto_lane ? <><dt>Auto lane</dt><dd>Sending from {row.auto_lane.sender_email} since {row.auto_lane.sent_on}</dd></> : null}
        </dl>
      </PanelSection>

      {/* The deals, lead deal first, then what closed before. */}
      <PanelSection title={open.length > 1 ? `Open opportunities (${open.length})` : 'Open opportunity'}>
        {lead
          ? [lead, ...open.filter(deal => deal !== lead)].map((deal, index) => <DealCard key={index} deal={deal} today={today} />)
          : <p className="panelempty">No open opportunity on this account.</p>}
      </PanelSection>
      {past.length ? (
        <PanelSection title="Past opportunities">
          {past.map((deal, index) => (
            <div key={index} className="aecontact">
              {deal.name || 'Unnamed opportunity'}
              <span className="muted"> · {dealOutcome(deal)}{deal.close_date ? ` · closed ${deal.close_date}` : ''}{deal.loss_reason || deal.disqualification_reason ? ` · ${deal.loss_reason || deal.disqualification_reason}` : ''}</span>
            </div>
          ))}
        </PanelSection>
      ) : null}

      {/* Who has worked it, and the collision warning: another rep on the account. */}
      <PanelSection title="Activity">
        <dl className="aefacts">
          <dt>Last SDR touch</dt>
          <dd>{dayWithAge(row.last_touch, today, 'Never')}{lastSdr?.who && lastSdr.day === row.last_touch ? <span className="muted"> · to {lastSdr.who}</span> : null}</dd>
          <dt>Last AE touch</dt><dd>{dayWithAge(row.last_touch_by_ae, today, 'Nothing logged')}</dd>
          <dt>SDR touches</dt>
          <dd>{formatCount(row.emails)} emails · {formatCount(row.calls)} calls · {formatCount(row.linkedin)} LinkedIn · {formatCount(row.responses)} replies</dd>
          {row.other_touches > 0 ? (
            <><dt>Other reps</dt><dd className="warn">{formatCount(row.other_touches)} {row.other_touches === 1 ? 'touch' : 'touches'} by {row.other_authors} other {row.other_authors === 1 ? 'rep' : 'reps'}{row.other_last_author ? `, last ${row.other_last_author}` : ''}{row.other_last_touch ? ` on ${row.other_last_touch}` : ''}</dd></>
          ) : null}
        </dl>
      </PanelSection>

      {/* The flags, with what each one means. */}
      {hasFlags ? (
        <PanelSection title="Flags">
          <dl className="aefacts">
            {hasSharedSlack(row) ? (
              <><dt>Shared Slack</dt><dd>We share a Slack Connect channel with them.{slackHref ? <> <a href={slackHref} target="_blank" rel="noopener">Open the channel</a></> : null}</dd></>
            ) : null}
            {pastOpportunity ? <><dt>Past opportunity</dt><dd>{pastOpportunityDetail(pastOpportunity)}</dd></> : null}
            {lost ? <><dt>Recent loss</dt><dd>A deal closed lost in {lost.when}.</dd></> : null}
            {row.competitor ? <><dt>Competitor</dt><dd>Salesforce marks this company a competitor. Nothing outbound acts on it.</dd></> : null}
            {q4 ? <><dt>Q4 target</dt><dd title={q4.title ?? undefined}>On a Q4 list, priority {q4.value}.</dd></> : null}
          </dl>
        </PanelSection>
      ) : null}

      {trends.length ? (
        <PanelSection title="Usage by org" aside="credits, last 30 days vs the 30 before">
          {trends.map(trend => <OrgRow key={trend.org.name} trend={trend} />)}
        </PanelSection>
      ) : null}

      <p className="tabsource">Sources: Salesforce, Gong, product usage. Read-only.</p>
    </div>
  );
}
